import { EAnalyticsTour } from "@frontend-shared/product-analytics";
import { EProjectPermission } from "@frontend-shared/types";
import { getHasAnyProjects } from "@frontend-shared/utils";
import { useDirection } from "@radix-ui/react-direction";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { Step } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";

import { useGetEnabledIntegrations } from "@/api/integration/useGetEnabledIntegrations";
import { useCompleteTour } from "@/api/user/useCompleteTour";
import { PATHS } from "@/constants/paths";
import { MainTourContext } from "@/contexts/MainTourContext";
import { useAgentsEnabled } from "@/hooks/use-agents-enabled";
import { useAuth } from "@/hooks/use-auth";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useExternalApiEnabled } from "@/hooks/use-external-api-enabled";
import { useIsBillingDisabled } from "@/hooks/use-is-billing-disabled";
import { useOnPremise } from "@/hooks/use-on-premise";
import { usePermissionCheck } from "@/hooks/use-permission";
import { useTourJoyrideCallback } from "@/hooks/use-tour-joyride-callback";
import { useTourLifecycle } from "@/hooks/use-tour-lifecycle";

import { TourShell } from "./TourShell";
import type { IStepNavigationProps, TMainRow } from "./types";
import {
  createTourStepBuilder,
  scrollMainSidebarToTop,
  scrollStepTargetIntoView,
} from "./utils";

const SETTINGS_MENU_ITEM_ID = "#settings-menu-item" as const;
// NOTE: The first step whose target only exists once the account menu is open.
// `userMenuStepIndex` is found from it, and `UserMenu` keeps the dropdown open
// for that step and the ones after it.
//
// NOTE: NSAI-6259 — notifications, because the project selector left the menu
// for the header in the same pass that brought notifications in. It has been
// each of the three in turn: Settings (selector in the header), the selector
// (NSAI-4345 moved it in), and now notifications. If a row moves in or out of
// this menu again, this constant and the `rows` order below move with it.
const USER_MENU_FIRST_ITEM_ID = "#notifications-menu-item" as const;

const MainTourProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { isOnPremise } = useOnPremise();
  const { isBillingDisabled } = useIsBillingDisabled();
  const { t } = useTranslation("tour");
  const dir = useDirection();
  const { mutate: completeTour } = useCompleteTour();
  const { user, isLoading, projects, isPersonalProject } = useAuth();
  const { data: enabledIntegrations = [] } = useGetEnabledIntegrations(!!user);
  const isCallAssistantFeaturesEnabled = useCallAssistantFeaturesEnabled();
  const { isExternalApiEnabled } = useExternalApiEnabled();
  const isAgentsEnabled = useAgentsEnabled();
  const [
    hasKnowledgeReadPermission,
    hasIntegrationReadPermission,
    hasAutomationReadPermission,
    hasAutomationManagerReadPermission,
    hasModelReadPermission,
    hasPrivateAssistantReadPermission,
    hasPublicAssistantReadPermission,
    hasPromptLibraryReadPermission,
    hasAllMembersAnalyticsReadPermission,
    hasAnalyticsReadPermission,
    hasProbeReadPermission,
  ] = usePermissionCheck([
    EProjectPermission.KNOWLEDGE_READ,
    EProjectPermission.INTEGRATION_READ,
    EProjectPermission.AUTOMATION_READ,
    EProjectPermission.AUTOMATION_MANAGER_READ,
    EProjectPermission.MODEL_READ,
    EProjectPermission.PRIVATE_ASSISTANT_READ,
    EProjectPermission.PUBLIC_ASSISTANT_READ,
    EProjectPermission.PROMPT_LIBRARY_READ,
    EProjectPermission.ALL_MEMBERS_ANALYTICS_READ,
    EProjectPermission.ANALYTICS_READ,
    EProjectPermission.PROBE_READ,
  ]);
  const hasEnabledIntegrations = enabledIntegrations.length > 0;
  const hasAnalyticsAccess =
    hasAllMembersAnalyticsReadPermission ||
    hasAnalyticsReadPermission ||
    hasProbeReadPermission;
  const hasAutomationAccess =
    hasAutomationReadPermission || hasAutomationManagerReadPermission;

  const sidebarPlacement = dir === "rtl" ? "left" : "right";
  const userMenuPlacement = dir === "rtl" ? "right" : "left";

  const steps: Step[] = useMemo(() => {
    const createStep = createTourStepBuilder(sidebarPlacement);

    const userMenuContent = isOnPremise
      ? t("steps.userMenu.description-on-premise")
      : t("steps.userMenu.description-default");
    const settingsContent = isOnPremise
      ? t("steps.settings.description-on-premise")
      : t("steps.settings.description-default");
    const chatHistoryPlace: Step["placement"] = `${sidebarPlacement}-start`;

    const isCa = isCallAssistantFeaturesEnabled;
    const isExtApi = isExternalApiEnabled;
    const hasModels = hasModelReadPermission;
    const hasAssistants =
      hasPrivateAssistantReadPermission || hasPublicAssistantReadPermission;
    const hasKnowledge = hasKnowledgeReadPermission;
    const hasPromptLib = hasPromptLibraryReadPermission;
    const canShowAgents = isAgentsEnabled && hasAutomationAccess;
    const canShowAnalytics = !isPersonalProject && hasAnalyticsAccess;
    const canShowInts = hasIntegrationReadPermission && hasEnabledIntegrations;
    const canShowBilling = !isOnPremise && !isBillingDisabled;
    const umPlace = userMenuPlacement;

    const rows: TMainRow[] = [
      // NOTE: NSAI-6820 — the sidebar rows in the order MainSidebar renders
      // them. Transcribe has no row of its own any more (it is an action of
      // the AI Call Assistant page), so it has no step here either.
      [true, "#sidebar-new-chat", "newChat"],
      [hasAssistants, "#sidebar-assistants", "assistants"],
      [canShowAgents, "#sidebar-agents", "agents"],
      [isCa, "#sidebar-call-assistant", "callAssistant"],
      [hasKnowledge, "#sidebar-knowledge", "knowledge"],
      [true, "#sidebar-generated-files", "generatedFiles"],
      // NOTE: NSAI-6589 — global search is available on every deployment now.
      [true, "#sidebar-search", "search"],
      [hasPromptLib, "#sidebar-prompt-library", "promptLibrary"],
      [canShowAnalytics, "#sidebar-security-analytics", "securityAnalytics"],
      [canShowInts, "#sidebar-integrations", "integrations"],
      [true, "#sidebar-chat-folders", "chatFolders"],
      [true, "#sidebar-chat-history", "chatHistory", chatHistoryPlace],
      [true, "#chat-model-selector", "chatModelSelector", "bottom-start"],
      [true, "#chat-input", "chatInput", "top"],
      // NOTE: NSAI-6259 — the project selector is a header control again, so
      // this step runs before the account menu opens. The notifications step
      // went the other way and now sits inside that menu, below.
      [true, "#project-selector", "projectSelector", "bottom-end"],
      // NOTE: NSAI-5822 — anchored ABOVE the account block, not below it.
      // NSAI-4345 moved the account row to the foot of the sidebar, and a
      // `bottom-end` tooltip on a target that sits on the bottom edge of the
      // viewport renders off the fold — react-floater clamps this tooltip
      // horizontally but not vertically, so the step spotlit the account row
      // with its explanation 177px below the screen.
      [true, "#user-menu", "userMenu", "top-start", userMenuContent],
      // NOTE: NSAI-6259 — notifications is a row of the account menu now, so
      // it is only reachable once that menu is open. It is also the FIRST such
      // row: `USER_MENU_FIRST_ITEM_ID` at the top of this file points at it,
      // and everything below here is inside the same open menu.
      [true, "#notifications-menu-item", "notifications", umPlace],
      [true, SETTINGS_MENU_ITEM_ID, "settings", umPlace, settingsContent],
      [true, "#organizations-menu-item", "organizations", umPlace],
      [true, "#preferences-menu-item", "preferences", umPlace],
      // NOTE: NSAI-6657 — Models is a Profile settings tab now, reached from
      // this menu rather than from the sidebar.
      [hasModels, "#models-menu-item", "models", umPlace],
      [isExtApi, "#api-keys-menu-item", "apiKeys", umPlace],
      [true, "#history-menu-item", "history", umPlace],
      [isCa, "#calendars-menu-item", "calendars", umPlace],
      [canShowBilling, "#billing-menu-item", "billing", umPlace],
      [true, "#faq-menu-item", "faq", umPlace],
      [true, "#docs-menu-item", "docs", umPlace],
    ];

    return rows
      .filter(([cond]) => cond)
      .map(([, target, key, placement, contentOverride]): Step => {
        let content: string;
        if (contentOverride !== undefined) {
          content = contentOverride;
        } else if (key === "userMenu" || key === "settings") {
          content = "";
        } else {
          content = t(`steps.${key}.description`);
        }

        return createStep({
          target,
          title: t(`steps.${key}.title`),
          content,
          placement,
        });
      });
  }, [
    hasAnalyticsAccess,
    hasPrivateAssistantReadPermission,
    hasPublicAssistantReadPermission,
    hasAutomationAccess,
    hasEnabledIntegrations,
    hasIntegrationReadPermission,
    hasKnowledgeReadPermission,
    hasModelReadPermission,
    hasPromptLibraryReadPermission,
    isAgentsEnabled,
    isBillingDisabled,
    isCallAssistantFeaturesEnabled,
    isExternalApiEnabled,
    isOnPremise,
    isPersonalProject,
    sidebarPlacement,
    t,
    userMenuPlacement,
  ]);

  const userMenuStepIndex = steps.findIndex(
    (step) => step.target === USER_MENU_FIRST_ITEM_ID,
  );

  const {
    isWelcomeOpen,
    setIsWelcomeOpen,
    isRunning,
    currentStep,
    setCurrentStep,
    startTour,
    handleStartActualTour,
    resetTour,
    handleWelcomeClose,
  } = useTourLifecycle({
    tour: EAnalyticsTour.MAIN,
    isCompleted: user?.isTourCompleted,
    completeTour,
  });

  const navigate = useNavigate();
  const location = useLocation();

  // NOTE: An archived chat replaces #chat-input with the ArchivedChatBanner,
  // breaking the composer step. Pull the user back to a fresh /chat before
  // Joyride starts so all targets are guaranteed to render.
  // NOTE: Reset sidebar scroll before flipping isRunning — the sidebar locks
  // its overflow while the tour runs, which would otherwise freeze any
  // pre-tour scroll position and place step 0 above the viewport.
  const handleStartActualTourOnFreshChat = useCallback((): void => {
    if (location.pathname !== PATHS.CHAT) {
      navigate(PATHS.CHAT);
    }
    scrollMainSidebarToTop();
    handleStartActualTour();
  }, [location.pathname, navigate, handleStartActualTour]);

  const handleStepNavigation = useCallback(
    ({ nextIndex }: IStepNavigationProps): void => {
      scrollStepTargetIntoView({ steps, stepIndex: nextIndex });
      setCurrentStep(nextIndex);
    },
    [steps, setCurrentStep],
  );

  const handleJoyrideCallback = useTourJoyrideCallback({
    steps,
    handleStepNavigation,
    resetTour,
    tour: EAnalyticsTour.MAIN,
    isRunning,
  });

  // NOTE: When the steps array changes mid-tour (e.g. async permissions load),
  // keep currentStep pointing at the same target so Joyride doesn't get lost.
  const previousStepsRef = useRef(steps);
  useEffect(() => {
    if (!isRunning || steps === previousStepsRef.current) {
      previousStepsRef.current = steps;
      return;
    }

    const currentTarget = previousStepsRef.current[currentStep]?.target as
      | string
      | undefined;
    previousStepsRef.current = steps;

    if (!currentTarget) return;

    const correctedIndex = steps.findIndex(
      (step) => step.target === currentTarget,
    );

    if (correctedIndex !== -1 && correctedIndex !== currentStep) {
      setCurrentStep(correctedIndex);
    }
  }, [steps, isRunning, currentStep, setCurrentStep]);

  useEffect(() => {
    if (isLoading || !user || !projects) return;

    const hasAnyProjects = getHasAnyProjects(projects);

    if (!user.isTourCompleted && hasAnyProjects) {
      setIsWelcomeOpen(true);
    }
  }, [isLoading, user, projects, setIsWelcomeOpen]);

  const tourContextValue = useMemo(
    () => ({
      startTour,
      currentStep,
      isRunning,
      userMenuStepIndex,
    }),
    [startTour, currentStep, isRunning, userMenuStepIndex],
  );

  return (
    <MainTourContext.Provider value={tourContextValue}>
      <TourShell
        isWelcomeOpen={isWelcomeOpen}
        onStartTour={handleStartActualTourOnFreshChat}
        onWelcomeClose={handleWelcomeClose}
        joyride={{
          steps,
          stepIndex: currentStep,
          isRunning,
          callback: handleJoyrideCallback,
        }}
      >
        {children}
      </TourShell>
    </MainTourContext.Provider>
  );
};

export default MainTourProvider;
